import {
  type Achievement,
  achievementsOf,
  type AchievementsEvent,
  BIOME_IDS,
  BIOMES,
  type BiomeId,
  getItem,
  getShopTitle,
  MobIdSchema,
  MOBS,
} from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import { element, kitHeading, kitIcon } from './kit.js';
import { formatCount } from './profile-format.js';
import { itemSlot } from './quest-rewards.js';
import { uiFormat, uiText } from './text.js';
import './quests.css';
import './achievements.css';

/**
 * The achievements tab of the profile window (ADR 0046): the title worn,
 * then one region at a time, each achievement with its bar, the region's
 * title at the top, and the feats apart. Nothing is decided here: progress
 * arrives counted, a click only asks.
 */

type Region = BiomeId | 'feats';
const REGIONS: readonly Region[] = [...BIOME_IDS, 'feats'];

export interface AchievementsTabOptions {
  onWear: (achievementId: string | null) => void;
  onClaim: (achievementId: string) => void;
  icons: () => CssAtlas | null;
}

/** The kit icon (`icon-<name>.png`) that says what an achievement asks. */
export function achievementIcon(achievement: Achievement): string {
  const { goal } = achievement;
  switch (goal.kind) {
    case 'kills': {
      const parsed = MobIdSchema.safeParse(goal.mobs[0]);
      const mob = parsed.success ? MOBS[parsed.data] : undefined;
      if (mob?.rank === 'zone-boss' || mob?.rank === 'dungeon-boss') return 'stat-bosses';
      return mob !== undefined && mob.rarity !== 'common' ? 'ach-elite' : 'stat-kills';
    }
    case 'portal':
      return 'portal';
    case 'hidden-chest':
      return 'ach-chest';
    case 'camp':
      return 'ach-camp';
    case 'bestiary':
      return 'stat-kills';
    case 'career':
      return {
        kills: 'stat-kills',
        deaths: 'stat-deaths',
        tilesWalked: 'stat-steps',
        playSeconds: 'stat-time',
        spellsCast: 'stat-spells',
        itemsLooted: 'stat-loot',
      }[goal.stat];
    case 'complete':
      return 'title';
    case 'complete-all':
      return 'achievements';
    case 'beta':
      return 'title';
  }
}

/** The count of rewards waiting to be claimed, in a corner of a button or a tab. */
export function claimBadge(count: number): HTMLElement {
  const badge = element('span', 'achievements__badge', String(count));
  badge.setAttribute('aria-hidden', 'true');
  return badge;
}

/** The region's full name, for the tooltip. */
function regionName(region: Region): string {
  return region === 'feats' ? 'Exploits' : BIOMES[region].name;
}

/** Its first word (« Forêt », « Pics ») on the tab: six tabs share a row. */
function regionLabel(region: Region): string {
  return regionName(region).split(' ')[0] ?? regionName(region);
}

export class AchievementsTab {
  readonly element: HTMLElement;
  private state?: AchievementsEvent;
  private region: Region = 'meadow';

  constructor(private readonly options: AchievementsTabOptions) {
    this.element = element('div', 'profile__tab-body achievements');
    this.render();
  }

  setState(state: AchievementsEvent): void {
    this.state = state;
    this.render();
  }

  private done(id: string): boolean {
    const entry = this.state?.progress[id];
    return entry !== undefined && entry.value >= entry.target;
  }

  /** A region's list as the player sees it: the beta's thanks only to those who have it (ADR 0083). */
  private shown(biome: BiomeId | undefined): Achievement[] {
    return achievementsOf(biome).filter(
      (achievement) => achievement.goal.kind !== 'beta' || this.done(achievement.id),
    );
  }

  /** Rewards earned and not yet claimed: what the badges count. */
  claimable(biome?: BiomeId | 'feats'): number {
    const regions = biome === undefined ? REGIONS : [biome];
    return regions
      .flatMap((region) => this.shown(region === 'feats' ? undefined : region))
      .filter(
        (achievement) =>
          achievement.reward !== undefined &&
          this.done(achievement.id) &&
          this.state?.claimed.includes(achievement.id) !== true,
      ).length;
  }

  private render(): void {
    this.element.replaceChildren(this.titles(), this.regionBar(), this.list());
  }

  /** The title worn, and every title earned to pick another. */
  private titles(): HTMLElement {
    const section = element('section', 'achievements__titles');
    const earned = [
      ...BIOME_IDS.flatMap((biome) => achievementsOf(biome)),
      ...achievementsOf(undefined),
    ].filter(
      (achievement) =>
        achievement.title !== undefined &&
        // A game master tries every title, earned or not (ADR 0046).
        (this.state?.anyTitle === true || this.done(achievement.id)),
    );
    // Titles that come with a shop offer, apart from the achievements.
    const bought = (this.state?.shopTitles ?? []).flatMap((id) => getShopTitle(id) ?? []);
    const count = earned.length + bought.length;
    section.append(kitHeading('Titre porté', uiFormat('{count} obtenus', { count })));
    const row = element('div', 'achievements__title-row');
    const worn = this.state?.title ?? null;
    const chip = (label: string, id: string | null): HTMLButtonElement => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'achievements__title-chip';
      button.classList.toggle('achievements__title-chip--worn', worn === id);
      button.setAttribute('aria-pressed', String(worn === id));
      button.textContent = uiText(label);
      button.addEventListener('click', () => {
        if (worn !== id) this.options.onWear(id);
      });
      return button;
    };
    row.append(chip('Aucun', null));
    for (const achievement of earned) row.append(chip(achievement.title ?? '', achievement.id));
    for (const title of bought) row.append(chip(title.title, title.id));
    section.append(row);
    if (count === 0) {
      section.append(
        element(
          'p',
          'login__hint profile__small',
          'Termine une région ou un exploit pour gagner un titre.',
        ),
      );
    }
    return section;
  }

  private regionBar(): HTMLElement {
    const bar = element('div', 'achievements__regions');
    bar.setAttribute('role', 'tablist');
    for (const region of REGIONS) {
      const list = this.shown(region === 'feats' ? undefined : region);
      const count = list.filter((achievement) => this.done(achievement.id)).length;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'achievements__region';
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-selected', String(region === this.region));
      button.classList.toggle('achievements__region--active', region === this.region);
      button.classList.toggle('achievements__region--done', count === list.length);
      button.title = uiText(regionName(region));
      button.append(
        element('span', 'achievements__region-name', regionLabel(region)),
        element('span', 'achievements__region-count', `${String(count)}/${String(list.length)}`),
      );
      const waiting = this.claimable(region);
      if (waiting > 0) button.append(claimBadge(waiting));
      button.addEventListener('click', () => {
        this.region = region;
        this.render();
      });
      bar.append(button);
    }
    return bar;
  }

  private list(): HTMLElement {
    const section = element('section', 'achievements__region-body');
    const biome = this.region === 'feats' ? undefined : this.region;
    const list = this.shown(biome);
    const completion = list.find((achievement) => achievement.goal.kind === 'complete');
    if (completion !== undefined && biome !== undefined) {
      const banner = element('div', 'achievements__banner');
      banner.classList.toggle('achievements__banner--done', this.done(completion.id));
      const text = element('div', 'achievements__banner-text');
      text.append(
        element('span', 'achievements__banner-title', completion.name),
        element(
          'span',
          'achievements__banner-reward',
          uiFormat('Récompense : titre « {title} »', { title: completion.title ?? '' }),
        ),
      );
      banner.append(kitIcon('title'), text, this.bar(completion.id));
      section.append(banner);
    }
    const rows = element('ol', 'achievements__list');
    for (const achievement of list) {
      if (achievement === completion) continue;
      rows.append(this.row(achievement));
    }
    section.append(rows);
    return section;
  }

  private bar(id: string): HTMLElement {
    const entry = this.state?.progress[id];
    const bar = element('span', 'achievements__bar');
    const fill = element('span', 'achievements__bar-fill');
    const share = entry === undefined || entry.target === 0 ? 0 : entry.value / entry.target;
    fill.style.width = `${String(Math.round(Math.min(1, share) * 100))}%`;
    const label = element(
      'span',
      'achievements__bar-label',
      entry === undefined ? '…' : `${formatCount(entry.value)} / ${formatCount(entry.target)}`,
    );
    bar.append(fill, label);
    return bar;
  }

  private row(achievement: Achievement): HTMLElement {
    const done = this.done(achievement.id);
    const row = element('li', 'achievements__row');
    row.classList.toggle('achievements__row--done', done);
    const text = element('div', 'achievements__text');
    text.append(
      element('span', 'achievements__name', achievement.name),
      element('span', 'achievements__description', achievement.description),
    );
    if (achievement.title !== undefined) {
      text.append(
        element(
          'span',
          'achievements__reward',
          uiFormat('Titre : {title}', { title: achievement.title }),
        ),
      );
    }
    const side = element('div', 'achievements__side');
    side.append(this.bar(achievement.id));
    if (achievement.reward !== undefined) {
      const claim = element('div', 'achievements__claim-row');
      claim.append(
        itemSlot({ templateId: achievement.reward, quantity: 1 }, this.options.icons()),
        this.claim(achievement, done),
      );
      side.append(claim);
    }
    row.append(kitIcon(achievementIcon(achievement)), text, side);
    return row;
  }

  /** An item reward: its name, then the button that asks for it once earned. */
  private claim(achievement: Achievement, done: boolean): HTMLElement {
    const reward = achievement.reward ?? '';
    const name = getItem(reward)?.name ?? reward;
    const claimed = this.state?.claimed.includes(achievement.id) === true;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'login__submit login__submit--gold achievements__claim';
    button.disabled = !done || claimed;
    button.textContent = claimed
      ? uiFormat('{name} : récupérée', { name })
      : uiFormat('Récupérer : {name}', { name });
    button.addEventListener('click', () => {
      this.options.onClaim(achievement.id);
    });
    return button;
  }
}
